import type { ChatAttachmentUploadResponse } from '../../types/krybot';
import { API_BASE_URL } from '../../config';

export interface ChatPanelAttachmentRecord {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  /** Linked company artifact item (from upload API or surface context). */
  artifactItemRef?: string;
  /** Legacy inline preview/send path — avoid on new sends */
  dataUrl?: string;
  /** Durable download path from upload API */
  downloadUrl?: string;
}

export interface PendingChatAttachment extends ChatPanelAttachmentRecord {
  previewUrl?: string;
  file?: File;
}

export const CHAT_PANEL_MAX_ATTACHMENTS = 8;
export const CHAT_PANEL_MAX_ATTACHMENT_BYTES = 512 * 1024;

export const CHAT_PANEL_ATTACHMENT_ACCEPT =
  'image/*,.pdf,.txt,.csv,.json,.md,.doc,.docx,.xls,.xlsx';

export function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function isImageAttachment(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export async function fileToPendingAttachment(file: File): Promise<PendingChatAttachment> {
  if (file.size > CHAT_PANEL_MAX_ATTACHMENT_BYTES) {
    throw new Error(
      `${file.name} is too large (max ${formatAttachmentSize(CHAT_PANEL_MAX_ATTACHMENT_BYTES)}).`
    );
  }

  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const base: PendingChatAttachment = {
    id,
    name: file.name,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    file,
  };

  if (isImageAttachment(base.mimeType)) {
    const previewUrl = await readFileAsDataUrl(file);
    return { ...base, previewUrl };
  }

  return base;
}

export function resolveAttachmentDownloadHref(record: ChatPanelAttachmentRecord): string | undefined {
  if (record.downloadUrl) {
    return record.downloadUrl.startsWith('http')
      ? record.downloadUrl
      : `${API_BASE_URL.replace(/\/$/, '')}${record.downloadUrl.startsWith('/') ? '' : '/'}${record.downloadUrl}`;
  }
  return record.dataUrl;
}

export function uploadedAttachmentToRecord(
  uploaded: ChatAttachmentUploadResponse
): ChatPanelAttachmentRecord {
  return {
    id: uploaded.attachment_id,
    name: uploaded.file_name,
    mimeType: uploaded.mime_type,
    size: uploaded.size_bytes,
    downloadUrl: uploaded.download_url,
    ...(uploaded.artifact_item_ref ? { artifactItemRef: uploaded.artifact_item_ref } : {}),
  };
}

export function buildAttachmentMessageMetadata(
  attachments: ChatPanelAttachmentRecord[]
): Record<string, unknown> {
  const artifactItemRefs = attachments
    .map((item) => item.artifactItemRef)
    .filter((ref): ref is string => Boolean(ref));

  return {
    attachment_ids: attachments.map((item) => item.id),
    attachments: attachments.map(({ id, name, mimeType, size, downloadUrl, artifactItemRef }) => ({
      id,
      name,
      mimeType,
      size,
      ...(downloadUrl ? { downloadUrl } : {}),
      ...(artifactItemRef ? { artifactItemRef } : {}),
    })),
    ...(artifactItemRefs.length > 0 ? { artifact_item_refs: artifactItemRefs } : {}),
  };
}

export function pendingToAttachmentRecord(pending: PendingChatAttachment): ChatPanelAttachmentRecord {
  return {
    id: pending.id,
    name: pending.name,
    mimeType: pending.mimeType,
    size: pending.size,
    downloadUrl: pending.downloadUrl,
    dataUrl: pending.dataUrl,
  };
}

export function attachmentsSummaryText(attachments: ChatPanelAttachmentRecord[]): string {
  if (attachments.length === 0) return '';
  if (attachments.length === 1) return attachments[0].name;
  return `${attachments.length} attachments`;
}

export function readAttachmentsFromMetadata(
  metadata: Record<string, unknown> | undefined
): ChatPanelAttachmentRecord[] {
  const raw = metadata?.attachments;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item): item is ChatPanelAttachmentRecord =>
      Boolean(item) &&
      typeof item === 'object' &&
      typeof (item as ChatPanelAttachmentRecord).id === 'string' &&
      typeof (item as ChatPanelAttachmentRecord).name === 'string'
  );
}
